import { useDispatch, useSelector } from 'react-redux';
import React, { useMemo } from 'react';
import { NodePoints } from './NodePoints';
import { useLocation } from 'react-router-dom';
import { ArrowUpRightIcon, TrashIcon } from '@iconicicons/react';
import {
  useAddNodePointMutation,
  useDeleteNodeMutation,
  useRemoveNodePointMutation,
  useUpdatePointMutation,
} from '../services/edgeApi';
import {
  selectAllNodes,
  selectAllPoints,
  selectIsMapLoading,
} from '../map/slice/mapSelectors';
import { mapActions } from '../map/slice/mapSlice';
import * as selectedItemsActions from '../selectedItems/selectedItemsSlice';
import toast from 'react-hot-toast';
import { ReusableModal } from '../modals/ReusableModal';
import { PointForm } from '../points/PointForm';
import { MoveIcon } from '../icons/MoveIcon';
import { UncontrolledTooltip } from 'reactstrap';
import { IconButton } from '../helpers/IconButton';

/**
 *
 * @param {{
 *  nodeId?: string | number,
 *  closePopup?: () => void,
 * }} props
 * @returns
 */
export const NodePopup = ({ nodeId, closePopup }) => {
  const dispatch = useDispatch();
  const [deleteNodeTrigger, deleteNodeRequestInfo] = useDeleteNodeMutation();
  const [addPointTrigger, addPointRequestInfo] = useAddNodePointMutation();
  const [removePointTrigger, removePointRequestInfo] =
    useRemoveNodePointMutation();
  const [updatePointTrigger, updatePointRequestInfo] = useUpdatePointMutation();

  const allPoints = useSelector(selectAllPoints);
  const allNodes = useSelector(selectAllNodes);
  const isMapLoading = useSelector(selectIsMapLoading);

  /** @type {import('../types/types').State<number | string | null>} */
  const [formPointId, setFormPointId] = React.useState(null);

  const node = useMemo(() => allNodes[nodeId], [allNodes, nodeId]);
  const nodePointsData = useMemo(
    () => node?.points.map((pointId) => allPoints[pointId]),
    [node, allPoints],
  );

  const location = useLocation();
  const isMapBuilder = location.pathname.includes('/map-builder');

  const tsps = useMemo(() => {
    return nodePointsData?.map((point) => point?.tsp_id).filter(Boolean) ?? [];
  }, [nodePointsData]);

  if (!node) {
    return null;
  }

  const deleteNode = async () => {
    if (
      confirm(
        `Are you sure you want to delete node #${node.id}?\n(points and components will not be deleted)`,
      )
    ) {
      try {
        await deleteNodeTrigger(node.id).unwrap();
        closePopup?.();
        toast.success('Node deleted successfully');
      } catch (error) {
        console.error('Failed to delete node:', error);
        toast.error(error?.data?.message ?? 'Failed to delete node');
      }
    }
  };

  const initiateEdgeCreation = async () => {
    if (!node) return;
    dispatch(mapActions.setEditorEdgeStartNode(node.id));
    closePopup?.();
  };

  const initiateNodeMove = async () => {
    if (!node) return;
    dispatch(mapActions.setEditorMoveNode(node.id));
    closePopup?.();
  };

  const addPoint = async (pointId) => {
    const point = allPoints[pointId];
    if (
      point.node !== null &&
      point.node !== undefined &&
      confirm(
        'You are trying to add a point that is already assigned to another node. This will remove the point from the previous node, proceed?',
      ) === false
    ) {
      return;
    }
    try {
      await addPointTrigger({ nodeId: node.id, pointId }).unwrap();
      toast.success('Point added successfully');
    } catch (error) {
      console.error('Failed to add point:', error);
      toast.error(error?.data?.message ?? 'Failed to add point');
    }
  };

  const removePoint = async (pointId) => {
    const point = allPoints[pointId];
    if (
      !confirm(
        `Are you sure you want to remove ${point?.name ?? pointId} from node #${
          node.id
        }?`,
      )
    ) {
      return;
    }
    try {
      await removePointTrigger({ nodeId: node.id, pointId }).unwrap();
      toast.success('Point removed successfully');
    } catch (error) {
      console.error('Failed to remove point:', error);
      toast.error(error?.data?.message ?? 'Failed to remove point');
    }
  };

  const updatePoint = async (pointId, patchData) => {
    const baseData = allPoints[pointId];
    try {
      await updatePointTrigger({ ...baseData, ...patchData }).unwrap();
      toast.success('Point updated successfully');
    } catch (error) {
      console.error('Failed to update point:', error);
      toast.error(error?.data?.message ?? 'Failed to update point');
    }
    setFormPointId(null);
  };

  return (
    <>
      <div
        className="flex-column w-100 overflow-auto pe-1"
        style={{
          maxHeight: '60vh',
          minWidth: 350,
          maxWidth: 600,
          overflowY: 'auto',
          paddingRight: -8,
        }}
      >
        <h4
          className="text-center bg-light pb-2"
          style={{
            position: 'sticky',
            top: -8,
            zIndex: 10,
          }}
        >
          {nodePointsData?.[0]?.tsp_name} node {node.id}
          {isMapBuilder && isMapLoading === false && (
            <>
              <span className="float-end">
                <IconButton
                  id="delete-node-button"
                  role="button"
                  onClick={deleteNode}
                  disabled={deleteNodeRequestInfo.isLoading}
                  disabledStyle={{ cursor: 'wait' }}
                  icon={TrashIcon}
                />
                <UncontrolledTooltip
                  target="delete-node-button"
                  placement="top"
                >
                  Delete Node
                </UncontrolledTooltip>
              </span>
              <span className="float-end">
                <ArrowUpRightIcon
                  id="create-edge-button"
                  role="button"
                  onClick={initiateEdgeCreation}
                />
                <UncontrolledTooltip
                  target="create-edge-button"
                  placement="top"
                >
                  Create Edge
                </UncontrolledTooltip>
              </span>
              <span className="float-end">
                <MoveIcon
                  id="move-node-button"
                  role="button"
                  width={24}
                  height={24}
                  onClick={initiateNodeMove}
                />
                <UncontrolledTooltip target="move-node-button" placement="top">
                  Move Node
                </UncontrolledTooltip>
              </span>
            </>
          )}
        </h4>
        <NodePoints
          lockedTsps={tsps}
          points={nodePointsData ?? []}
          editable={
            isMapBuilder &&
            [
              isMapLoading,
              addPointRequestInfo.isLoading,
              removePointRequestInfo.isLoading,
            ].every((val) => val === false)
          }
          onRemovePoint={removePoint}
          onEditPoint={setFormPointId}
          onAddPoint={addPoint}
          onSelectPoint={(pointId) =>
            dispatch(selectedItemsActions.itemClicked({
              id: pointId,
              itemType: 'point',
            }))
          }
        />
      </div>
      <ReusableModal
        size="lg"
        isOpen={formPointId !== null}
        onClose={() => setFormPointId(null)}
      >
        <h4 className="text-center">
          {formPointId ? `Edit Point #${formPointId}` : 'Add Point to Node'}
        </h4>
        <PointForm
          pointId={formPointId}
          onSave={updatePoint}
          onCancel={() => setFormPointId(null)}
        />
      </ReusableModal>
    </>
  );
};
